import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreatePolygon } from "@babylonjs/core/Meshes/Builders/polygonBuilder";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import * as MyEarcut from "earcut";
import { AdditionGeometry } from "./AdditionGeometry";
import { Util } from "../../utils/Util";
import { WallAddition } from "../WallAddition";
import { PI_DIV2 } from "@egr/wcf/modules/utils/math";
import { StringProperty, PropertyChangedResult } from "@egr/wcf/modules/core/prop";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { perpendicularVector2 } from "@egr/wcf/modules/core/utils/MathUtils";
export class Radiator extends AdditionGeometry {
    getType() {
        return ("Radiator");
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.9;
        tParams.height = 0.5;
        tParams.depth = 0.1;
        tParams.wallOffset = 0.05;
        tParams.floorOffset = 0.2;
        tParams.color = "#ffffff";
        return (tParams);
    }
    create2dGeometry(pParent) {
        this.createRadiator2d(pParent, this.prepare2dMesh);
    }
    create3dGeometry(pParent) {
        this.createRadiator(pParent, this.prepare3dMesh);
    }
    getProperties() {
        let tProperties = new Array();
        let tColorChoices = new Array();
        for (let tCol of this.mRoomManager._wallColors)
            tColorChoices.push(this.mRoomManager._getWallColorPropValue(tCol));
        //color
        let tColorRProp = new StringProperty(this.mAddition, Radiator.PROP_KEY_COLOR, this.mRoomManager.resources.propertyColor);
        tColorRProp.choiceList = true;
        tColorRProp.getChoices = () => Promise.resolve(tColorChoices);
        tColorRProp.getValue = () => this.mRoomManager._getWallColorPropValue(this.mAddition.color);
        tColorRProp.setValue = (pValue) => {
            if (pValue != null) {
                let tColor = StringProperty.ConvertValueToString(pValue);
                this.mAddition.setColor(tColor);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tColorRProp);
        return (tProperties);
    }
    getEOXDescription(pDocument, pParentNode, pRootNode, pWall) {
        let tAttribsList = new Array();
        tAttribsList = [];
        tAttribsList.push({ qualifiedName: "structure_type", value: "Radiator" });
        let tRoomElementNode = Util._xmlAddChildNode(pDocument, pRootNode, "object", "", tAttribsList);
        let tThermostat_side = thermostat.right;
        // define category
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "category", "Other", null);
        // position
        tAttribsList = [];
        let tPos = this.mAddition.position.clone();
        if (this.mAddition.wall != null) {
            let tWall = this.mAddition.wall;
            let tWT = tWall.thickness * 0.5;
            let tWO = this.mAddition.wallOffset;
            let tAD = this.mAddition.depth * 0.5;
            let tV_perp = perpendicularVector2(tWall.wallDirection).normalize();
            tV_perp.scaleInPlace(tWO + tWT + tAD);
            if (this.mAddition.mirrorHorizontal) {
                tPos.subtractInPlace(tV_perp);
                if (this.mAddition.mirrorVertical)
                    tThermostat_side = thermostat.left;
                else
                    tThermostat_side = thermostat.right;
            }
            else {
                tPos.addInPlace(tV_perp);
                if (this.mAddition.mirrorVertical)
                    tThermostat_side = thermostat.left;
            }
        }
        tAttribsList.push({ qualifiedName: "x", value: (tPos.x).toString() });
        tAttribsList.push({ qualifiedName: "y", value: "0" });
        tAttribsList.push({ qualifiedName: "z", value: (-tPos.y).toString() });
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "position", "", tAttribsList);
        // rotation
        let t3dRotation = this.mAddition.node.rotationQuaternion;
        tAttribsList = [];
        if (t3dRotation != null) {
            tAttribsList.push({ qualifiedName: "x", value: (-t3dRotation.x).toString() });
            tAttribsList.push({ qualifiedName: "y", value: (-t3dRotation.z).toString() });
            tAttribsList.push({ qualifiedName: "z", value: (-t3dRotation.y).toString() });
            tAttribsList.push({ qualifiedName: "w", value: (-t3dRotation.w).toString() });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "rotation", "", tAttribsList);
        }
        else {
            tAttribsList.push({ qualifiedName: "x", value: "0" });
            tAttribsList.push({ qualifiedName: "y", value: "0" });
            tAttribsList.push({ qualifiedName: "z", value: "0" });
            tAttribsList.push({ qualifiedName: "w", value: "1" });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "rotation", "", tAttribsList);
        }
        // width
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "width", this.mAddition.width.toString(), null);
        // height
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "height", this.mAddition.height.toString(), null);
        // thickness
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "thickness", this.mAddition.depth.toString(), null);
        // parapet
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "installationHeight", this.mAddition.floorOffset.toString(), null);
        // thermostat (installation side)
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "thermostat", tThermostat_side.toString(), null);
        // color
        tAttribsList = [];
        if (this.mAddition.color.trim() != '') {
            let tcolor = Util.hexToRGB(this.mAddition.color);
            tAttribsList.push({ qualifiedName: "r", value: tcolor.r.toString() });
            tAttribsList.push({ qualifiedName: "g", value: tcolor.g.toString() });
            tAttribsList.push({ qualifiedName: "b", value: tcolor.b.toString() });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "color", "", tAttribsList);
        }
    }
    createRadiator(pParent, callback) {
        let tW = this.mAddition.width - Radiator.THERMOSTAT_LENGTH;
        let tH = this.mAddition.height;
        let tD = this.mAddition.depth;
        let tWO = this.mAddition.wallOffset;
        let tWT = this.mAddition.WallThickness;
        this.mRoomManager.createColorMaterial(this.mAddition.color);
        let tMatId = "color" + this.mRoomManager.materialSplitStr + this.mAddition.color;
        let tRadiator = CreateBox("", {
            width: tW,
            height: tH,
            depth: tD
        }, this.mScene);
        if (tRadiator === null)
            return;
        tRadiator.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tRadiator.position.x = -Radiator.THERMOSTAT_LENGTH * 0.5;
        if (this.mAddition.mirrorHorizontal)
            tRadiator.position.z = -tRadiator.position.z;
        if (this.mAddition.mirrorVertical)
            tRadiator.position.x = Radiator.THERMOSTAT_LENGTH * 0.5;
        tRadiator.isPickable = true;
        tRadiator.position.y = tH * 0.5;
        tRadiator.material = this.mRoomManager.MatManager.getMaterial(tMatId);
        tRadiator.parent = pParent;
        this.mAddition.setGeometryOffset_Z(tRadiator.position.z);
        callback(tRadiator);
        this.thermostat(tRadiator);
    }
    createRadiator2d(pParent, callback) {
        let tHW = (this.mAddition.width * 0.5) - (Radiator.THERMOSTAT_LENGTH * 0.5);
        let tHD = (this.mAddition.depth * 0.5);
        let tD = this.mAddition.depth;
        let tWT = this.mAddition.WallThickness;
        let tWO = this.mAddition.wallOffset;
        // radiator shape
        let tPlane_radiator = CreatePlane("", { width: tHW * 2, height: tD }, this.mScene);
        tPlane_radiator.rotation.x = PI_DIV2;
        tPlane_radiator.parent = pParent;
        tPlane_radiator.material = this.mRoomManager.app.model.materials.defaultMaterial;
        tPlane_radiator.position.x -= 0.03;
        tPlane_radiator.position.y = -pParent.position.y + WallAddition.WALLADDITION_2D_LINE_HEIGHT;
        tPlane_radiator.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tPlane_radiator.isPickable = true;
        // thermostat shape
        let tshapeThermostate = [
            new Vector3(0, 0, 0.025),
            new Vector3(0.06, 0, 0.015),
            new Vector3(0.06, 0, -0.015),
            new Vector3(0, 0, -0.025),
            new Vector3(0, 0, 0.025)
        ];
        let tPlane_Thermostat = CreatePolygon("room:WallAddition_radiator_thermostat", { shape: tshapeThermostate, sideOrientation: Mesh.DOUBLESIDE }, this.mScene, MyEarcut);
        tPlane_Thermostat.isPickable = true;
        tPlane_Thermostat.material = this.mRoomManager.app.model.materials.defaultMaterial;
        tPlane_Thermostat.position.y = -pParent.position.y + WallAddition.WALLADDITION_2D_LINE_HEIGHT;
        tPlane_Thermostat.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tPlane_Thermostat.position.x = tHW - (Radiator.THERMOSTAT_LENGTH * 0.5);
        tPlane_Thermostat.parent = pParent;
        tPlane_Thermostat.renderingGroupId = 0;
        tPlane_Thermostat.freezeWorldMatrix();
        // radiator outline
        let tLines = CreateLines("", {
            points: [
                new Vector3(-tHW, 0.0, -tHD),
                new Vector3(-tHW, 0.0, tHD),
                new Vector3(tHW, 0.0, tHD),
                new Vector3(tHW, 0.0, -tHD),
                new Vector3(-tHW, 0.0, -tHD)
            ],
            updatable: false,
            instance: null
        }, this.mScene);
        tLines.isPickable = true;
        tLines.intersectionThreshold = 0;
        tLines.color = Color3.Black();
        tLines.position.y = -pParent.position.y + WallAddition.WALLADDITION_2D_LINE_HEIGHT;
        tLines.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tLines.position.x -= 0.03;
        tLines.parent = pParent;
        tLines.renderingGroupId = 0;
        tLines.freezeWorldMatrix();
        // thermostat outline
        let tLines1 = CreateLines("", {
            points: [
                new Vector3(0, 0.0, 0.025),
                new Vector3(0.06, 0.0, 0.015),
                new Vector3(0.06, 0.0, -0.015),
                new Vector3(0, 0.0, -0.025),
                new Vector3(0, 0.0, 0.025)
            ],
            updatable: false,
            instance: null
        }, this.mScene);
        tLines1.isPickable = true;
        tLines1.intersectionThreshold = 0;
        tLines1.color = Color3.Black();
        tLines1.position.y = -pParent.position.y + WallAddition.WALLADDITION_2D_LINE_HEIGHT;
        tLines1.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tLines1.position.x = tHW - (Radiator.THERMOSTAT_LENGTH * 0.5);
        tLines1.parent = pParent;
        tLines1.renderingGroupId = 0;
        tLines1.freezeWorldMatrix();
        if (this.mAddition.mirrorHorizontal) {
            tPlane_radiator.position.z = -tPlane_radiator.position.z;
            tPlane_Thermostat.position.z = -tPlane_Thermostat.position.z;
            tLines.position.z = -tLines.position.z;
            tLines1.position.z = -tLines1.position.z;
        }
        if (this.mAddition.mirrorVertical) {
            tPlane_radiator.position.x += Radiator.THERMOSTAT_LENGTH;
            tPlane_Thermostat.scaling.x = -1;
            tPlane_Thermostat.position.x = -tHW + (Radiator.THERMOSTAT_LENGTH * 0.5);
            tLines.position.x += Radiator.THERMOSTAT_LENGTH;
            tLines1.scaling.x = -1;
            tLines1.position.x = -tHW + (Radiator.THERMOSTAT_LENGTH * 0.5);
        }
        ShadowGenerator.disableMeshShadow(tPlane_radiator);
        ShadowGenerator.disableMeshShadow(tPlane_Thermostat);
        ShadowGenerator.disableMeshShadow(tLines);
        ShadowGenerator.disableMeshShadow(tLines1);
        callback(tPlane_radiator);
        callback(tPlane_Thermostat);
        callback(tLines);
        callback(tLines1);
    }
    thermostat(pParent) {
        if (pParent == null)
            return;
        let tW = this.mAddition.width - Radiator.THERMOSTAT_LENGTH;
        let tH = this.mAddition.height;
        let tThermostat = CreateCylinder("", {
            height: 0.06,
            diameterBottom: 0.05,
            diameterTop: 0.03
        }, this.mScene);
        if (tThermostat === null)
            return;
        tThermostat.position.x = tW * 0.5 + 0.03;
        let tAngle = (0 - 90) / 180 * Math.PI;
        if (this.mAddition.mirrorVertical) {
            tThermostat.position.x = -tThermostat.position.x;
            tAngle = (180 - 90) / 180 * Math.PI;
        }
        let tAxis = new Vector3(0, 0, 1);
        let tQuaternion = Quaternion.RotationAxis(tAxis, tAngle);
        tThermostat.rotationQuaternion = tQuaternion;
        tThermostat.position.y = tH * 0.5 - 0.05;
        tThermostat.position.z = 0;
        if (this.mAddition.mirrorHorizontal)
            tThermostat.position.z = -tThermostat.position.z;
        tThermostat.isPickable = true;
        tThermostat.material = this.getWhitePlasticMaterial();
        tThermostat.parent = pParent;
        this.prepare3dMesh(tThermostat);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Radiator.PROP_KEY_COLOR = "room:RoomElement:color";
Radiator.THERMOSTAT_LENGTH = 0.06;
Radiator.THERMOSTAT_DIAMETER_TOP = 0.03;
Radiator.THERMOSTAT_DIAMETER_BOTTOM = 0.05;
const thermostat = {
    right: 'right',
    left: 'left',
    none: 'none'
};
